import { useLazyQuery } from '@apollo/client'
import config from 'config'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'

import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import PanelSwitchField from 'shared/components/PanelSwitchField'
import ChiliVariables from 'shared/components/Tools/services/ChiliVariables'
import { CoverTypes } from 'shared/types/global'
import Flex from 'shared/ui/Flex'
import Input from 'shared/ui/Input'
import Select from 'shared/ui/Select'

const variablesApi = ChiliVariables.getInstance()

const COVER_TYPE_OPTIONS = [
  {
    labelId: 'Panel.coverStartEditing.setup.options.soft',
    value: CoverTypes.soft
  },
  {
    labelId: 'Panel.coverStartEditing.setup.options.hard',
    value: CoverTypes.hard
  }
]


const DEFAULT_PAGES_COUNT = 101
const CoverStartEditingPanelCoverSetup = () => {
  const { formatMessage } = useIntl()
  const { projectId } = useParams()

  const [isAutoBased, setIsAutoBased] = useState(false)
  const [coverTypeIndex, setCoverTypeIndex] = useState<number | null>(
    null
  )
  const [pagesCount, setPagesCount] = useState<string>('')

  const [fetchProjectMeta] = useLazyQuery(GET_PROJECT_META)

  const fetchVaraibles = async () => {
    const coverType = await variablesApi.getVariable(
      config.themes.variablesKeys.cover.coverType
    )

    const pagesCount = await variablesApi.getVariable(
      config.themes.variablesKeys.cover.pagesCount
    )

    const isPagesValZero = pagesCount.value === "0"

    if(isPagesValZero) {
      variablesApi.setVariableValue(
        config.themes.variablesKeys.cover.pagesCount,
        DEFAULT_PAGES_COUNT
      )
    }

    setCoverTypeIndex(coverType === 'softcover' ? 0 : 1)
    setPagesCount(isPagesValZero ? DEFAULT_PAGES_COUNT.toString() : pagesCount.value.toString())
  }

  const adjustVariables = async () => {
    const { data } = await fetchProjectMeta({
      variables: { projectId: projectId || '' }
    })
    const meta = data?.getProjectMeta

    if (!meta) {
      return
    }

    const { pagesCount, coverType } = meta

    await variablesApi.setVariableValue(
      config.themes.variablesKeys.cover.pagesCount,
      pagesCount
    )
    setPagesCount(pagesCount ? pagesCount.toString() : '1')

    await variablesApi.setVariableValue(
      config.themes.variablesKeys.cover.coverType,
      coverType === CoverTypes.soft ? 'softcover' : 'hardcover'
    )
    setCoverTypeIndex(coverType === CoverTypes.soft ? 0 : 1)
  }

  const handleCoverTypeChange = (index: number) => {
    setCoverTypeIndex(index)
    variablesApi.setVariableValue(
      config.themes.variablesKeys.cover.coverType,
      COVER_TYPE_OPTIONS[index].value
    )
  }

  const handlePagesCountChange = (value: string) => {
    setPagesCount(value)
    variablesApi.setVariableValue(
      config.themes.variablesKeys.cover.pagesCount,
      value
    )
  }

  useEffect(() => {
    if (isAutoBased) {
      return
    }

    fetchVaraibles()
  }, [])

  useEffect(() => {
    if (!isAutoBased) {
      return
    }

    adjustVariables()
  }, [isAutoBased])

  return (
    <div>
      <PanelSwitchField
        labelId="Panel.startEditing.coverSetup.autoBasedOnProject"
        value={isAutoBased}
        onChange={() => setIsAutoBased(!isAutoBased)}
      />

      <Flex flex={1} justifyContent="space-between" marginTop={8}>
        <Select
          isDisabled={isAutoBased}
          width={142}
          activeIndex={coverTypeIndex ?? 0}
          onChange={(_, index) => handleCoverTypeChange(index)}
          options={COVER_TYPE_OPTIONS.map(option =>
            formatMessage({ id: option.labelId })
          )}
        />

        <Input
          isDisabled={isAutoBased}
          width={100}
          value={pagesCount}
          onChange={handlePagesCountChange}
          placeholder="Pages"
          size="small"
          type="number"
        />
      </Flex>
    </div>
  )
}

export default CoverStartEditingPanelCoverSetup
